import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  notificationsApi,
  type Notification,
  type NotificationType,
  formatNotificationTime,
  getNotificationColor,
  getNotificationCategory,
} from '@/services/notifications';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import styles from './NotificationsPage.module.css';

type FilterType = 'all' | NotificationType;
type ReadFilter = 'all' | 'unread' | 'read';
type CategoryFilter = 'all' | 'workflow' | 'records' | 'compliance' | 'security' | 'system';

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        perPage: 20,
      };
      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }
      if (readFilter === 'unread') {
        params.isRead = false;
      } else if (readFilter === 'read') {
        params.isRead = true;
      }

      const response = await notificationsApi.list(params);
      if (response.success && response.data) {
        setNotifications(response.data);
        if (response.meta) {
          setTotalPages(response.meta.totalPages);
        }
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, readFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [notifications]);

  async function handleMarkAsRead(id: string) {
    try {
      const response = await notificationsApi.markAsRead(id);
      if (response.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }

  async function handleMarkAllAsRead() {
    try {
      const response = await notificationsApi.markAllAsRead();
      if (response.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      const response = await notificationsApi.delete(id);
      if (response.success) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  }

  async function handleDeleteSelected() {
    try {
      for (const id of selectedIds) {
        await notificationsApi.delete(id);
      }
      setNotifications((prev) => prev.filter((n) => !selectedIds.has(n.id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to delete selected notifications:', err);
    }
  }

  async function handleDeleteAllRead() {
    try {
      const response = await notificationsApi.deleteAllRead();
      if (response.success) {
        fetchNotifications();
      }
    } catch (err) {
      console.error('Failed to delete read notifications:', err);
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  function toggleSelectAll() {
    // Filter notifications by category before selecting
    const visibleNotifications = notifications.filter((n) => {
      if (categoryFilter === 'all') return true;
      return getNotificationCategory(n.type) === categoryFilter;
    });

    if (selectedIds.size === visibleNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleNotifications.map((n) => n.id)));
    }
  }

  function getNotificationLink(notification: Notification): string | null {
    const data = notification.data;

    switch (notification.type) {
      case 'workflow_assigned':
      case 'workflow_delegated':
      case 'task_due':
        return '/tasks';
      case 'workflow_approved':
      case 'workflow_rejected':
      case 'record_created':
      case 'record_updated':
        return data?.documentId ? `/records?doc=${data.documentId}` : '/records';
      // Compliance alerts
      case 'compliance_retention_due':
      case 'compliance_review_required':
      case 'compliance_hold_placed':
      case 'compliance_hold_released':
        return '/compliance';
      // Security alerts
      case 'security_access_violation':
      case 'security_suspicious_activity':
      case 'security_login_failed':
      case 'security_permission_changed':
        return '/security-audit';
      // AI/Anomaly alerts
      case 'anomaly_detected':
      case 'anomaly_resolved':
        return data?.staffId ? `/records?staff=${data.staffId}` : '/ai-features';
      default:
        return null;
    }
  }

  function renderNotificationIcon(type: string) {
    switch (type) {
      case 'workflow_assigned':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
        );
      case 'workflow_approved':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        );
      case 'workflow_rejected':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'workflow_delegated':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        );
      case 'record_created':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <line x1="9" y1="15" x2="15" y2="15" />
          </svg>
        );
      case 'record_updated':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        );
      case 'task_due':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'system_announcement':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
        );
      // Compliance icons
      case 'compliance_retention_due':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <circle cx="12" cy="16" r="2" />
            <path d="M12 14v-2" />
          </svg>
        );
      case 'compliance_review_required':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3l8-8" />
            <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
          </svg>
        );
      case 'compliance_hold_placed':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        );
      case 'compliance_hold_released':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 9.9-1" />
          </svg>
        );
      // Security icons
      case 'security_access_violation':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        );
      case 'security_suspicious_activity':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'security_login_failed':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            <line x1="18" y1="8" x2="23" y2="13" />
            <line x1="23" y1="8" x2="18" y2="13" />
          </svg>
        );
      case 'security_permission_changed':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
          </svg>
        );
      // AI/Anomaly icons
      case 'anomaly_detected':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <circle cx="12" cy="12" r="3" />
            <path d="M12 8v1" />
            <path d="M12 15v1" />
            <path d="M8 12h1" />
            <path d="M15 12h1" />
          </svg>
        );
      case 'anomaly_resolved':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        );
      default:
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
    }
  }

  // Filter notifications by category (client-side since API might not support category filter)
  const filteredNotifications = notifications.filter((n) => {
    if (categoryFilter === 'all') return true;
    return getNotificationCategory(n.type) === categoryFilter;
  });

  const unreadCount = filteredNotifications.filter((n) => !n.isRead).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Notifications</h1>
          <p className={styles.subtitle}>
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up'}
          </p>
        </div>
        <div className={styles.headerActions}>
          {unreadCount > 0 && (
            <Button variant="secondary" onClick={handleMarkAllAsRead}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value as CategoryFilter);
              setTypeFilter('all');
              setPage(1);
            }}
          >
            <option value="all">All Categories</option>
            <option value="workflow">Workflow & Tasks</option>
            <option value="records">Records</option>
            <option value="compliance">Compliance</option>
            <option value="security">Security & AI</option>
            <option value="system">System</option>
          </Select>
          <Select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as FilterType);
              setPage(1);
            }}
          >
            <option value="all">All types</option>
            <optgroup label="Workflow">
              <option value="workflow_assigned">Task Assigned</option>
              <option value="workflow_approved">Workflow Approved</option>
              <option value="workflow_rejected">Workflow Rejected</option>
              <option value="workflow_delegated">Task Delegated</option>
              <option value="task_due">Task Due</option>
            </optgroup>
            <optgroup label="Records">
              <option value="record_created">Record Created</option>
              <option value="record_updated">Record Updated</option>
            </optgroup>
            <optgroup label="Compliance">
              <option value="compliance_retention_due">Retention Due</option>
              <option value="compliance_review_required">Review Required</option>
              <option value="compliance_hold_placed">Hold Placed</option>
              <option value="compliance_hold_released">Hold Released</option>
            </optgroup>
            <optgroup label="Security">
              <option value="security_access_violation">Access Violation</option>
              <option value="security_suspicious_activity">Suspicious Activity</option>
              <option value="security_login_failed">Login Failed</option>
              <option value="security_permission_changed">Permission Changed</option>
              <option value="anomaly_detected">Anomaly Detected</option>
              <option value="anomaly_resolved">Anomaly Resolved</option>
            </optgroup>
            <optgroup label="System">
              <option value="system_announcement">Announcements</option>
            </optgroup>
          </Select>
          <Select
            value={readFilter}
            onChange={(e) => {
              setReadFilter(e.target.value as ReadFilter);
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </Select>
        </div>
        <div className={styles.bulkActions}>
          {selectedIds.size > 0 && (
            <>
              <span className={styles.selectedCount}>{selectedIds.size} selected</span>
              <Button variant="ghost" size="sm" onClick={handleDeleteSelected}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete selected
              </Button>
            </>
          )}
          {notifications.some((n) => n.isRead) && (
            <Button variant="ghost" size="sm" onClick={handleDeleteAllRead}>
              Clear read
            </Button>
          )}
        </div>
      </div>

      <div className={styles.notificationsContainer}>
        {loading ? (
          <div className={styles.loading}>Loading notifications...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p>No notifications found</p>
            {(typeFilter !== 'all' || readFilter !== 'all' || categoryFilter !== 'all') && (
              <Button
                variant="secondary"
                onClick={() => {
                  setTypeFilter('all');
                  setReadFilter('all');
                  setCategoryFilter('all');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.listHeader}>
              <label className={styles.selectAll}>
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredNotifications.length && filteredNotifications.length > 0}
                  onChange={toggleSelectAll}
                />
                <span>Select all</span>
              </label>
            </div>
            <div className={styles.list}>
              {filteredNotifications.map((notification) => {
                const link = getNotificationLink(notification);
                return (
                  <div
                    key={notification.id}
                    className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''} ${selectedIds.has(notification.id) ? styles.selected : ''}`}
                  >
                    <div className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(notification.id)}
                        onChange={() => toggleSelect(notification.id)}
                      />
                    </div>
                    <div
                      className={styles.notificationIcon}
                      style={{ color: getNotificationColor(notification.type) }}
                    >
                      {renderNotificationIcon(notification.type)}
                    </div>
                    <div className={styles.notificationContent}>
                      <div className={styles.notificationHeader}>
                        <span className={styles.notificationTitle}>{notification.title}</span>
                        <span className={styles.notificationTime}>
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className={styles.notificationMessage}>{notification.message}</p>
                      {link && (
                        <Link to={link} className={styles.notificationLink}>
                          View details
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </Link>
                      )}
                    </div>
                    <div className={styles.notificationActions}>
                      {!notification.isRead && (
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleMarkAsRead(notification.id)}
                          title="Mark as read"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                      )}
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleDelete(notification.id)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
